import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { z } from "zod";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { Badge } from "~/components/ui/badge";
import { OtpInput } from "~/components/ui/otp-input";
import { Calendar, CheckCircle, UserPlus } from "lucide-react";
import { cn } from "~/lib/cn";
import {
  getNextAvailableSummitOptions,
  checkUserSummitRegistrationOptions,
  registerAuthenticatedUserForSummit,
  sendMagicLinkRegistration,
  completeSummitRegistrationAfterAuth,
} from "~/lib/functions/summit-registration";
import { format, isSameDay } from "date-fns";

const RegistrationFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
});

const VerificationCodeSchema = z.object({
  code: z.string().min(6, "Code must be 6 digits").max(6, "Code must be 6 digits"),
});

type RegistrationForm = z.infer<typeof RegistrationFormSchema>;
type VerificationCodeForm = z.infer<typeof VerificationCodeSchema>;

interface SummitRegistrationProps {
  redirectUrl?: string;
  className?: string;
}

type RegistrationState = "form" | "loading" | "code-sent" | "verify-code" | "registered" | "success";

function formatSummitDateRange(startDate: Date, endDate: Date): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isSameDay(start, end)) {
    return format(start, "MMMM d, yyyy");
  }
  
  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return `${format(start, "MMMM d")} - ${format(end, "d, yyyy")}`;
  }
  
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, "MMMM d")} - ${format(end, "MMMM d, yyyy")}`;
  }
  
  return `${format(start, "MMMM d, yyyy")} - ${format(end, "MMMM d, yyyy")}`;
}

export function SummitRegistration({
  redirectUrl,
  className,
}: SummitRegistrationProps) {
  const { isSignedIn, userId } = useAuth();
  const clerk = useClerk();
  const queryClient = useQueryClient();
  const [registrationState, setRegistrationState] = useState<RegistrationState>("form");
  const [pendingSignUp, setPendingSignUp] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState<string[]>([]);

  const {
    data: summitData,
    isLoading: summitLoading,
    error: summitError,
  } = useQuery(getNextAvailableSummitOptions);

  const {
    data: userRegistration,
    isLoading: registrationLoading,
    error: registrationError,
  } = useQuery({
    ...checkUserSummitRegistrationOptions,
    queryKey: ["summit", "user-registration", userId || "anonymous"], // Make key dependent on userId
    enabled: Boolean(isSignedIn && userId),
    retry: false, // Don't retry failed auth requests
  });

  // Extract summit from response (handle new return format)
  const summit = summitData?.summit;
  const summitApiError = summitData?.error;

  const form = useForm<RegistrationForm>({
    resolver: zodResolver(RegistrationFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const codeForm = useForm<VerificationCodeForm>({
    resolver: zodResolver(VerificationCodeSchema),
    defaultValues: {
      code: "",
    },
  });

  const { register, handleSubmit, formState: { errors }, reset } = form;

  const authenticatedRegisterMutation = useMutation({
    mutationFn: registerAuthenticatedUserForSummit,
    onSuccess: (data) => {
      // Invalidate user registration query to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["summit", "user-registration"] });
      
      setRegistrationState("success");
      if (data.redirectUrl) {
        setTimeout(() => {
          window.location.href = data.redirectUrl!;
        }, 2000);
      }
    },
    onError: (error) => {
      console.error("Authenticated registration failed:", error);
      setRegistrationState("form");
    },
  });

  const magicLinkMutation = useMutation({
    mutationFn: sendMagicLinkRegistration,
    onSuccess: async (data) => {
      try {
        if (!clerk) {
          throw new Error("Authentication service not available. Please try again.");
        }

        if (!clerk.client) {
          throw new Error("Authentication service not ready. Please refresh the page and try again.");
        }

        // Generate secure random password
        const securePassword = crypto.getRandomValues(new Uint8Array(32))
          .reduce((acc, byte) => acc + byte.toString(16).padStart(2, '0'), '');

        // Create sign-up with email verification code and password
        const signUp = await clerk.client.signUp.create({
          emailAddress: data.registrationData.email,
          firstName: data.registrationData.firstName,
          lastName: data.registrationData.lastName,
          password: securePassword,
        });

        // Send verification code via email
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });

        // Store the signUp object to complete verification later
        setPendingSignUp(signUp);
        setRegistrationState("verify-code");
        reset();
      } catch (error) {
        console.error("Failed to send magic link:", error);
        setRegistrationState("form");
        // Re-throw to trigger onError handler
        throw error;
      }
    },
    onError: (error) => {
      console.error("Magic link registration failed:", error);
      setRegistrationState("form");
    },
  });

  const completeRegistrationMutation = useMutation({
    mutationFn: completeSummitRegistrationAfterAuth,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["summit", "user-registration"] });
      setRegistrationState("success");
      if (data.redirectUrl) {
        setTimeout(() => {
          window.location.href = data.redirectUrl!;
        }, 2000);
      }
    },
  });

  const verifyCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!pendingSignUp) {
        throw new Error("No pending sign-up found");
      }
      
      // Verify the code
      const result = await pendingSignUp.attemptEmailAddressVerification({
        code,
      });

      if (result.status === "complete") {
        // Set the active session
        await clerk.setActive({ session: result.createdSessionId });
        
        // Wait for auth state to actually update
        await new Promise((resolve) => {
          const checkAuth = () => {
            if (clerk.user) {
              resolve(undefined);
            } else {
              setTimeout(checkAuth, 50);
            }
          };
          checkAuth();
        });
        
        // Now call the authenticated server function
        const summitRegistration = await completeSummitRegistrationAfterAuth({
          data: {
            summitId: summit!.id,
            redirectUrl,
          },
        });
        
        return summitRegistration;
      } else {
        // Return the actual error details from Clerk
        const errorDetails = result.errors?.map((e: { message: string }) => e.message).join(", ") || "Verification failed";
        throw new Error(errorDetails);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["summit", "user-registration"] });
      setRegistrationState("success");
      if (data.redirectUrl) {
        setTimeout(() => {
          window.location.href = data.redirectUrl!;
        }, 2000);
      }
    },
    onError: (error) => {
      console.error("Code verification failed:", error);
    },
  });

  // Check URL params for post-auth registration completion
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const summitId = urlParams.get('summitId');
    const postAuthRedirectUrl = urlParams.get('redirectUrl');
    
    if (summitId && isSignedIn && !userRegistration?.isRegistered) {
      // Complete registration after successful auth
      completeRegistrationMutation.mutate({
        data: {
          summitId,
          redirectUrl: postAuthRedirectUrl || undefined,
        },
      });
    }
  }, [isSignedIn, userRegistration, completeRegistrationMutation]);

  const handleAuthenticatedRegistration = () => {
    // Prevent double-submission
    if (registrationState === "loading" || authenticatedRegisterMutation.isPending) {
      return;
    }
    
    setRegistrationState("loading");
    authenticatedRegisterMutation.mutate({ data: { redirectUrl } });
  };

  const handleMagicLinkRegistration = (formData: RegistrationForm) => {
    // Prevent double-submission
    if (registrationState === "loading" || magicLinkMutation.isPending) {
      return;
    }
    
    setRegistrationState("loading");
    magicLinkMutation.mutate({ data: { ...formData, redirectUrl } });
  };

  const handleOtpComplete = (code: string) => {
    if (verifyCodeMutation.isPending) {
      return;
    }
    
    verifyCodeMutation.mutate(code);
  };

  // Show loading state with timeout protection
  if (summitLoading || (isSignedIn && registrationLoading)) {
    return (
      <div className={cn("text-center", className)}>
        <div className="animate-spin mx-auto w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full" />
        <p className="mt-4 text-text-secondary">Loading summit information...</p>
      </div>
    );
  }

  // Show error state with retry option (only show registration errors for authenticated users)
  const hasError = summitError || summitApiError || (isSignedIn && registrationError);
  if (hasError) {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-lg font-heading font-semibold mb-2">Something went wrong</h3>
        <p className="text-text-secondary mb-4">
          {summitError?.message || (isSignedIn && registrationError?.message) || summitApiError || "Unable to load summit information. Please try again later."}
        </p>
        <Button
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ["summit"] });
            setRegistrationState("form");
          }}
          variant="outline"
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (!summit) {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-text-secondary mb-4">
          <Calendar className="w-12 h-12 mx-auto" />
        </div>
        <h3 className="text-lg font-heading font-semibold mb-2">No Upcoming Summit</h3>
        <p className="text-text-secondary">
          There are no summits scheduled at the moment. Please check back later for updates.
        </p>
      </div>
    );
  }

  // Authenticated user who is already registered
  if (isSignedIn && userRegistration?.isRegistered) {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-green-500 mb-4">
          <CheckCircle className="w-12 h-12 mx-auto" />
        </div>
        <h3 className="text-xl font-heading font-semibold mb-2">You're Already Registered!</h3>
        <p className="text-text-secondary mb-4">
          You're all set for <strong>{summit.title}</strong>
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="text-sm text-text-secondary">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Calendar className="w-4 h-4" />
              <span>{formatSummitDateRange(summit.startDate, summit.endDate)}</span>
            </div>
            <div className="text-xs">
              {format(new Date(summit.startDate), "h:mm a")} - {format(new Date(summit.endDate), "h:mm a")}
            </div>
          </div>
        </div>
        {redirectUrl && (
          <Button
            onClick={() => window.location.href = redirectUrl}
            className="w-full"
          >
            Continue
          </Button>
        )}
      </div>
    );
  }

  // Success state
  if (registrationState === "success") {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-green-500 mb-4">
          <CheckCircle className="w-12 h-12 mx-auto" />
        </div>
        <h3 className="text-xl font-heading font-semibold mb-2">Registration Successful!</h3>
        <p className="text-text-secondary mb-4">
          You're now registered for <strong>{summit.title}</strong>
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="text-sm text-text-secondary">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Calendar className="w-4 h-4" />
              <span>{formatSummitDateRange(summit.startDate, summit.endDate)}</span>
            </div>
            <div className="text-xs">
              {format(new Date(summit.startDate), "h:mm a")} - {format(new Date(summit.endDate), "h:mm a")}
            </div>
          </div>
        </div>
        {redirectUrl ? (
          <p className="text-sm text-text-secondary">
            Redirecting you shortly...
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            We'll send you more details about the summit via email.
          </p>
        )}
      </div>
    );
  }


  // Verification code entry state
  if (registrationState === "verify-code") {
    return (
      <div className={className}>
        <div className="space-y-2">
          <h1 className="font-heading text-3xl">Check your email</h1>
          <h3 className="text-text-secondary font-heading">
            We sent a code to your email. Please enter it below to complete your registration for <strong>{summit.title}</strong>.
          </h3>
        </div>
        <div className="my-8 flex items-end gap-4 pl-1 transition-all duration-300 ease-in-out">
          <OtpInput
            value={verificationCode}
            onChange={setVerificationCode}
            onComplete={handleOtpComplete}
            error={verifyCodeMutation.error?.message}
          />
        </div>
        <Button
          variant="ghost"
          onClick={() => setRegistrationState("form")}
          className="w-full"
        >
          Back to Form
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="text-center mb-6">
        <div className="text-brand-500 mb-4">
          <UserPlus className="w-12 h-12 mx-auto" />
        </div>
        <h2 className="text-2xl font-heading font-bold mb-2">
          Join {summit.title}
        </h2>
        <p className="text-text-secondary mb-4">
          Register for free and get access to exclusive content and networking opportunities.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="text-sm text-text-secondary">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Calendar className="w-4 h-4" />
              <span>{formatSummitDateRange(summit.startDate, summit.endDate)}</span>
            </div>
            <div className="text-xs">
              {format(new Date(summit.startDate), "h:mm a")} - {format(new Date(summit.endDate), "h:mm a")}
            </div>
          </div>
        </div>
        <Badge variant="secondary" className="mb-6">
          Free Registration
        </Badge>
      </div>

      {isSignedIn ? (
        // Authenticated user - simple registration
        <div className="space-y-4">
          <p className="text-center text-text-secondary">
            You're signed in and ready to register!
          </p>
          <Button
            onClick={handleAuthenticatedRegistration}
            loading={registrationState === "loading" || authenticatedRegisterMutation.isPending}
            disabled={registrationState === "loading" || authenticatedRegisterMutation.isPending}
            className="w-full"
            size="lg"
          >
            {registrationState === "loading" || authenticatedRegisterMutation.isPending ? "Registering..." : "Register for Summit"}
          </Button>
        </div>
      ) : (
        // Unauthenticated user - magic link registration
        <form onSubmit={handleSubmit(handleMagicLinkRegistration)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="First Name"
              error={errors.firstName?.message}
            >
              <Input
                {...register("firstName")}
                placeholder="First name"
                error={errors.firstName?.message}
              />
            </FormField>
            <FormField
              label="Last Name"
              error={errors.lastName?.message}
            >
              <Input
                {...register("lastName")}
                placeholder="Last name"
                error={errors.lastName?.message}
              />
            </FormField>
          </div>
          <FormField
            label="Email Address"
            error={errors.email?.message}
          >
            <Input
              {...register("email")}
              type="email"
              placeholder="your@email.com"
              error={errors.email?.message}
            />
          </FormField>
          <Button
            type="submit"
            loading={registrationState === "loading" || magicLinkMutation.isPending}
            disabled={registrationState === "loading" || magicLinkMutation.isPending}
            className="w-full"
            size="lg"
          >
            {registrationState === "loading" || magicLinkMutation.isPending ? "Registering..." : "Register for Summit"}
          </Button>
          <p className="text-xs text-center text-text-secondary">
            We'll send you a secure link to complete your registration. No password required!
          </p>
        </form>
      )}

      {(authenticatedRegisterMutation.error || magicLinkMutation.error) && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            {authenticatedRegisterMutation.error?.message || magicLinkMutation.error?.message}
          </p>
        </div>
      )}
    </div>
  );
}